import {
  type FC,
  type PropsWithChildren,
  memo,
  useEffect,
  useState
} from 'react';
import { type AxiosResponse } from 'axios';
import { Box, Grid, Typography } from '@mui/material';
import moment, { type Moment } from 'moment';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { NetworkRequest } from '../../../../Utilities/NetworkRequests/NetworkRequests';
import LoaderComponent from '../../../../Utilities/LoaderComponent';
import { useTYWallCarriersLeadDemographicsItemStyles } from './utils/styles';
import PieChartComponent from '../../../../Charts/PieChart';
import { logError } from '../../../../../Utilities/logError';
import { TYWallCarriersLeadDemographicsState } from './utils/state';
import { LeadDemographicTable } from '../../../../Affiliates/Components/LeadDemographic/components/LeadDemographicTable';
import { addPercentToItems } from '../../../../Affiliates/Components/LeadDemographic/components/utils';
import { formatAffiliateValue } from '../../../../Utilities/getAffiliateIDFromName';

interface IStoreState {
  dateRange: {
    startDate: Moment;
    endDate: Moment;
    dateRange: string;
  };
  filters: {
    site: string;
    affiliateID: string;
  };
}

interface IProps {
  carrier: string;
  buyer?: string;
}

interface IItemProps {
  label: string;
  loading: boolean;
  data: Array<{ dataKey: string; nameKey: string }>;
}

type DataType = Record<string, Array<{ dataKey: string; nameKey: string }>>;

export const TYWallCarriersLeadDemographicsStoreState = () => {
  return useSelector(
    createSelector(
      (state: IStoreState) => {
        const { startDate, endDate, dateRange } = state?.dateRange ?? {};

        return {
          startDate,
          endDate,
          dateRange,
          site: state?.filters?.site ?? '0',
          affiliateID: formatAffiliateValue(
            state?.filters?.affiliateID ?? 'all'
          )
        };
      },
      (obj) => obj
    )
  );
};

export const TYWallCarriersLeadDemographics: FC<IProps> = memo(
  ({ carrier, buyer }) => {
    const { startDate, endDate, dateRange, site, affiliateID } =
      TYWallCarriersLeadDemographicsStoreState();

    const [availableCategories, setAvailableCategories] = useState<
      Array<{ label: string; dataKey: string }>
    >(TYWallCarriersLeadDemographicsState);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DataType>({});

    useEffect(() => {
      (async () => {
        try {
          setLoading(true);
          const {
            data
          }: AxiosResponse<
            Record<string, Array<{ wins: string; label: string }>>
          > = await NetworkRequest(
            'reports',
            {
              carrier,
              buyer: buyer || undefined,
              start_date: moment(startDate).format('YYYY-MM-DD'),
              end_date: moment(endDate).format('YYYY-MM-DD'),
              date_range: dateRange,
              affiliate_id: affiliateID,
              site_id: site
            },
            'getDemographicsBidsAll'
          );

          const dataCategories = Object.keys(data);

          setAvailableCategories(
            TYWallCarriersLeadDemographicsState.filter((item) =>
              dataCategories.includes(item.dataKey)
            )
          );

          setData(
            Object.keys(data).reduce((acc, key) => {
              return {
                ...acc,
                [key]: data[key].map((item) => ({
                  dataKey: item.wins,
                  nameKey: item.label
                }))
              };
            }, {} as DataType)
          );
        } catch (error) {
          logError(error);
        } finally {
          setLoading(false);
        }
      })();
    }, [affiliateID, buyer, carrier, dateRange, endDate, site, startDate]);

    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ marginBottom: '5rem' }}
      >
        {availableCategories.map((item) => (
          <TYWallCarriersLeadDemographicsItem
            key={item.label}
            loading={loading}
            label={item.label}
            data={data[item.dataKey] || []}
          />
        ))}
      </Grid>
    );
  }
);

const TYWallCarriersLeadDemographicsItem: FC<IItemProps> = memo(
  ({ loading, label, data }) => {
    const { classes } = useTYWallCarriersLeadDemographicsItemStyles();

    return (
      <DataItemBody label={label} loading={loading}>
        {!data?.length ? (
          <Typography variant="body1" color="textSecondary">
            No data available
          </Typography>
        ) : (
          <Grid item xs={12} className={classes.pieAndTableWrapper}>
            <PieChartComponent
              width={300}
              data={data}
              dataKey="dataKey"
              nameKey="nameKey"
              hideLowLabel={false}
              formatValue={false}
              notChangeNameKey
              multiColor
              additionalLabel="Winning Bids"
            />
            <div className={classes.table}>
              <LeadDemographicTable
                rows={addPercentToItems(data)}
                value={data}
                label={label}
                columns={[
                  {
                    name: 'nameKey',
                    title: label
                  },
                  {
                    name: 'percent',
                    title: 'Percent'
                  }
                ]}
              />
            </div>
          </Grid>
        )}
      </DataItemBody>
    );
  }
);

const DataItemBody: FC<PropsWithChildren<{ loading: boolean; label: string }>> =
  memo(({ children, loading, label }) => {
    const { classes } = useTYWallCarriersLeadDemographicsItemStyles();

    return (
      <Grid item xs={12} className={classes.container}>
        <Typography className={classes.blockTitle}>{label}</Typography>
        <Box className={classes.chartContainer}>
          {loading ? <LoaderComponent /> : children}
        </Box>
      </Grid>
    );
  });
